import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import type { RangeMeses } from '../lib/calcularComparativoCustomizado';
import { mesReferenciaParaData } from '../../../../src/lib/financeiro/periodo';

interface Props {
  label: string;
  value: RangeMeses;
  onChange: (novo: RangeMeses) => void;
  mesesDisponiveis: string[]; // ordenados cronologicamente, formato "MES/ANO"
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const SeletorPeriodoCalendario: React.FC<Props> = ({ label, value, onChange, mesesDisponiveis }) => {
  const [aberto, setAberto] = useState(false);
  const [anoExibido, setAnoExibido] = useState(
    () => mesReferenciaParaData(value.fim)?.getFullYear() ?? new Date().getFullYear()
  );
  const [selecaoInicio, setSelecaoInicio] = useState(value.inicio);
  const [selecaoFim, setSelecaoFim] = useState<string | null>(value.fim);
  const containerRef = useRef<HTMLDivElement>(null);

  const disponiveisSet = useMemo(() => new Set(mesesDisponiveis), [mesesDisponiveis]);
  const anos = useMemo(() => {
    const set = new Set<number>();
    mesesDisponiveis.forEach((m) => {
      const d = mesReferenciaParaData(m);
      if (d) set.add(d.getFullYear());
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [mesesDisponiveis]);
  const anoMin = anos[0] ?? anoExibido;
  const anoMax = anos[anos.length - 1] ?? anoExibido;

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAberto(false);
        setSelecaoInicio(value.inicio);
        setSelecaoFim(value.fim);
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto, value]);

  const abrir = () => {
    setSelecaoInicio(value.inicio);
    setSelecaoFim(value.fim);
    setAnoExibido(mesReferenciaParaData(value.fim)?.getFullYear() ?? new Date().getFullYear());
    setAberto(true);
  };

  const clicarMes = (mes: string) => {
    if (!disponiveisSet.has(mes)) return;
    if (selecaoFim !== null) {
      // já tinha início+fim de uma seleção anterior (ou recém-aberto): começa uma nova
      setSelecaoInicio(mes);
      setSelecaoFim(null);
      return;
    }
    const dInicio = mesReferenciaParaData(selecaoInicio);
    const dClicado = mesReferenciaParaData(mes);
    if (dInicio && dClicado && dClicado.getTime() < dInicio.getTime()) {
      setSelecaoFim(selecaoInicio);
      setSelecaoInicio(mes);
    } else {
      setSelecaoFim(mes);
    }
  };

  const aplicar = () => {
    onChange({ inicio: selecaoInicio, fim: selecaoFim ?? selecaoInicio });
    setAberto(false);
  };

  const cancelar = () => {
    setSelecaoInicio(value.inicio);
    setSelecaoFim(value.fim);
    setAberto(false);
  };

  const emPreview = (mes: string): boolean => {
    if (selecaoFim === null) return false;
    const d = mesReferenciaParaData(mes);
    const ini = mesReferenciaParaData(selecaoInicio);
    const fim = mesReferenciaParaData(selecaoFim);
    if (!d || !ini || !fim) return false;
    return d.getTime() >= ini.getTime() && d.getTime() <= fim.getTime();
  };

  const naBorda = (mes: string): boolean => mes === selecaoInicio || mes === selecaoFim;

  const rotulo = value.inicio === value.fim ? value.inicio : `${value.inicio} – ${value.fim}`;

  return (
    <div className="relative" ref={containerRef}>
      <span className="text-xs font-bold text-gray-500 dark:text-gray-400 block mb-1">{label}:</span>
      <button
        type="button"
        onClick={() => (aberto ? setAberto(false) : abrir())}
        className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <Calendar size={14} className="text-gray-400" />
        {rotulo}
      </button>

      {aberto && (
        <div className="absolute z-20 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              disabled={anoExibido <= anoMin}
              onClick={() => setAnoExibido((a) => a - 1)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold text-gray-800 dark:text-white">{anoExibido}</span>
            <button
              type="button"
              disabled={anoExibido >= anoMax}
              onClick={() => setAnoExibido((a) => a + 1)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-1.5 mb-3">
            {MESES.map((m) => {
              const mesRef = `${m}/${anoExibido}`;
              const habilitado = disponiveisSet.has(mesRef);
              const selecionado = naBorda(mesRef);
              const destacadoPreview = !selecionado && emPreview(mesRef);
              return (
                <button
                  key={m}
                  type="button"
                  disabled={!habilitado}
                  onClick={() => clicarMes(mesRef)}
                  className={`text-xs font-semibold rounded-lg py-1.5 transition-colors ${
                    !habilitado
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : selecionado
                      ? 'bg-primary text-white'
                      : destacadoPreview
                      ? 'bg-primary/15 text-primary dark:text-primary'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={cancelar}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={aplicar}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-dark"
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeletorPeriodoCalendario;
